import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../../endpoints';
import { FormQuestion, FormQuestionWithOptions } from './formQuestions';

export interface FormSection {
    _id: string;
    title: string;
    templateId: string;
    order: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface FormSectionWithQuestions extends FormSection {
    questions: FormQuestionWithOptions[];
}

export interface FormSectionsResponse {
    message: string;
    data: FormSection[];
}

export interface FormSectionResponse {
    message: string;
    data: FormSection;
}

export interface CreateFormSectionBody {
    title?: string;
    templateId: string;
    order?: number;
}

export interface UpdateFormSectionBody {
    title?: string;
    order?: number;
}

export interface MoveFormSectionBody {
    newOrder: number;
}

// CREATE
export const createFormSection = async (data: CreateFormSectionBody): Promise<FormSectionResponse> => {
    try {
        const response: AxiosResponse<FormSectionResponse> = await apiClient.post('/form-sections', data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// DUPLICATE
export const duplicateFormSection = async (sectionId: string): Promise<FormSectionResponse> => {
    try {
        const response: AxiosResponse<FormSectionResponse> = await apiClient.post(`/form-sections/${sectionId}/duplicate`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// UPDATE
export const updateFormSection = async (sectionId: string, data: UpdateFormSectionBody): Promise<FormSectionResponse> => {
    try {
        const response: AxiosResponse<FormSectionResponse> = await apiClient.patch(`/form-sections/${sectionId}`, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// GET SECTIONS BY TEMPLATE ID
export const getSectionsByTemplateId = async (templateId: string): Promise<FormSectionWithQuestions[] | FormSection[]> => {
    try {
        const response: AxiosResponse<{ message: string; data: any[] }> = await apiClient.get(`/form-sections/template/${templateId}`);

        return response.data.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// MOVE
export const moveFormSection = async (sectionId: string, data: MoveFormSectionBody): Promise<FormSectionResponse> => {
    try {
        const response: AxiosResponse<FormSectionResponse> = await apiClient.patch(`/form-sections/${sectionId}/move`, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// DELETE ONLY
export const deleteFormSection = async (sectionId: string): Promise<{ message: string }> => {
    try {
        const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/form-sections/${sectionId}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// DELETE WITH CONTENT
export const deleteFormSectionWithContent = async (sectionId: string): Promise<{ message: string }> => {
    try {
        const response: AxiosResponse<{ message: string }> = await apiClient.delete(`/form-sections/${sectionId}/with-content`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};
